<template>
  <div class="position-relative" :class="{ focused: focused && !discreetInputs }">
    <component
      :is="tag"
      ref="editableRef"
      contenteditable="true"
      @input="handleInput($event)"
      @blur="handleUpdateModelValue($event, 'change'), emit('blur'), (focused = false)"
      @focus="moveCaretToEnd($event), emit('focus'), (focused = true)"
    />
    <component :is="tag" v-if="!staged && placeholder" class="placeholder-text" :class="placeholderClass">{{ placeholder }}</component>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, getCurrentInstance } from 'vue'

// -----------
// -- Setup --
// -----------
const props = withDefaults(
  defineProps<{
    tag?: string
    modelValue: string
    placeholder?: string
    discreetInputs?: boolean
    maxLength?: number
    placeholderClass?: Record<string, boolean>
  }>(),
  { tag: 'div' },
)

const emit = defineEmits(['update:modelValue', 'change', 'input', 'blur', 'focus'])

const instance = getCurrentInstance()
const modelModifiers = instance?.attrs?.modelModifiers as { lazy?: boolean; trim?: boolean } | undefined

// --------------------------
// -- Reactives & computed --
// --------------------------
const editableRef = ref<HTMLElement>()
const staged = ref('')
const focused = ref(false)

// --------------------------------
// -- Watchers & Lifecycle hooks --
// --------------------------------
watch(
  () => props.modelValue,
  (newValue) => {
    if (editableRef.value && editableRef.value.innerText.trim() !== newValue.trim()) {
      // makes sure to clear the field first on input when not focused
      editableRef.value.innerText = newValue
      staged.value = newValue
    }
  },
)

onMounted(() => {
  if (editableRef.value) {
    staged.value = editableRef.value.innerText = props.modelValue
  }
})

// ---------------
// -- Functions --
// ---------------

const handleInput = (event: InputEvent) => {
  const element = event.target as HTMLInputElement
  const effectiveMaxLength = props.maxLength ? Math.max(element.innerText.length - 1, props.maxLength) : undefined // limits the value length to property setting or current value length if the current value is longer
  const value = element.innerText?.slice(0, effectiveMaxLength)
  staged.value = modelModifiers?.trim ? value.trim() : value

  if (!props.maxLength || element.innerText.length <= props.maxLength) {
    element.value = staged.value
  } else if (editableRef.value) {
    // replace element innertext if too many chars
    element.innerText = staged.value!
    moveCaretToEnd(event)
  }
  handleUpdateModelValue(event, 'input')
}

const handleUpdateModelValue = (event: Event, eventType: 'input' | 'change') => {
  emit(eventType, event)

  if (modelModifiers?.lazy ? eventType === 'change' : eventType === 'input') {
    emit('update:modelValue', staged.value)
  }
}

const moveCaretToEnd = (e: InputEvent | FocusEvent) => {
  const el = e.target as HTMLElement
  const range = document.createRange()
  const selection = window.getSelection()

  range.selectNodeContents(el)
  range.collapse(false) // Collapse the range to the end (false means end)
  selection?.removeAllRanges() // Clear any existing selections
  selection?.addRange(range) // Set the new range to be at the end
}
</script>

<style lang="scss" scoped>
.placeholder-text {
  position: absolute;
  width: max-content;
  top: 0;
  padding-top: inherit;
  pointer-events: none;
}

*[contenteditable='true']:focus {
  border: none;
  outline: none;
}
</style>
